// Company intelligence, the page (module 10 U52 to U59; B517), in the grammar of the pages U28 has reached
// (triage, the item page, Tasks): the title in score type with the days as tabs, the counts in score
// figures, then the read-out as cards, each headed in headline type (B530). No rank figures: they are the
// ten's, and a line is news, not a to-do (U57). Intelligence > Company.
//
// One column as wide as the ten (the Tasks rule, U48): what came of the reader's hand-ons first (U55), then
// each company where something moved, most important first (U52). A line is news, not a decision: no
// yellow, nothing puts it in the ten (U57), and it is read only (U58): pressing it opens the messages,
// chats and calls it was written from, below it. Nothing on the page explains itself (U32): an empty day
// says what would fill it, in one line.
//
// A failed load is a line with the API's own words and "Try again"; nothing is swallowed. A line whose
// sources the reader may no longer open is not in the answer at all (R5), so nothing stands in for it.
//
// B1694 (Mike, 2026-09-23, U188), added to the page and changing nothing already on it: a date search beside the
// tabs reaches any day with a read-out, back to the first; and below the newest read-out, the previous two weeks,
// a day each, that open in place.
import { ActionSelect, ActionInput, ActionButton, CardFrame, Chip, CompanyLine, CompanyName, EmptyState, List, ListRow, SectionHeading } from '../parts/index.js';
import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../api.js';
import { useLiveRefresh } from '../live.js';
import { companyIntel, lineSources, type CompanyIntel as View, type HandOnView, type LineView, type SourceView } from './api.js';
import { countWord, coveredWords, dayHeading, daySummaryWords, dayTab, morningHour, newTag, noReadoutWords, recentDays, sourceChips, todayOf, whenOf, writtenWords } from './words.js';
import { useCompanyLine } from '../company/lines.js';
import './intel.css';

type Load = { kind: 'loading' } | { kind: 'ready'; view: View } | { kind: 'error'; message: string };
export type Sources = { kind: 'loading' } | { kind: 'ready'; list: SourceView[] } | { kind: 'error'; message: string };

/** How many days are tabs; the rest of the thirty the API lists are in "Older". */
const TABS = 5;

const problem = (e: unknown) => (e instanceof ApiError ? e.message : 'Cobrain could not be reached');

export function SourceList({ s, go }: { s: Sources; go: (path: string) => void }) {
  if (s.kind === 'loading') return <div className="ci-src"><p className="ci-muted">Opening what it was written from…</p></div>;
  if (s.kind === 'error') return <div className="ci-src"><p className="error" role="alert">{s.message}</p></div>;
  return (
    <div className="ci-src">
      {s.list.map((x) => (
        <div key={x.signalId} className="ci-src-item">
          <div className="ci-src-meta">
            <Chip>{x.kind === 'email' ? 'Email' : x.kind === 'meeting' || x.kind === 'call' ? 'Call' : 'Chat'}</Chip>
            {x.from ? <span className="ci-src-who">{x.from}</span> : null}
            {x.meetingId ? <a className="ci-link" href={`/meetings/${x.meetingId}`} onClick={(e) => { e.preventDefault(); go(`/meetings/${x.meetingId}`); }}>Open the call</a> : null}
            <span className="ci-src-at">{whenOf(x.at)}</span>
          </div>
          {x.subject ? <p className="ci-src-subject">{x.subject}</p> : null}
          {x.text ? <p className="ci-src-text">{x.text}</p> : null}
        </div>
      ))}
    </div>
  );
}

/** One line of the read-out. `named` is false where the card already names the colleague (a hand-on's "to Will"). */
function Line({ line, open, onToggle, sources, go, named = true }: { line: LineView; open: boolean; onToggle: () => void; sources: Sources | undefined; go: (path: string) => void; named?: boolean }) {
  const tag = newTag(line);
  const names = named && line.people.length > 0 ? line.people.join(', ') : null;
  return (
    <ListRow className={open ? 'ci-line open' : 'ci-line'}>
      <ActionButton reading type="button" className="ci-line-btn" aria-expanded={open} onClick={onToggle}>
        <span className="ci-text">{tag ? <Chip className="ci-new">{tag}</Chip> : null}{line.text}</span>
        <span className="ci-row">
          {sourceChips(line.sources).map((c) => <Chip key={c}>{c}</Chip>)}
          {names ? <span>{names}</span> : null}
          {whenOf(line.happenedAt) ? <span>{whenOf(line.happenedAt)}</span> : null}
          <span className="ci-open">{open ? 'Close' : 'Sources'}</span>
        </span>
      </ActionButton>
      {open && sources ? <SourceList s={sources} go={go} /> : null}
    </ListRow>
  );
}

/** One day's read-out: the counts, the span, the hand-ons and the companies, as the page has always drawn them. */
function Readout({ v, latest, idp = '', open, toggle, sources, go }: { v: View; latest: boolean; idp?: string; open: string | null; toggle: (id: string) => void; sources: Record<string, Sources>; go: (path: string) => void }) {
  const covered = coveredWords(v);
  const lineCount = v.companies.reduce((n, c) => n + c.lines.length, 0) + v.handOns.length;
  const morning = morningHour(v);
  return (
    <>
          <p className="ci-counts">
              <span><b className="big">{v.companies.length}</b> {countWord(v.companies.length, 'company', 'companies')} moved</span>
              {v.handOns.length > 0 ? <span><b className="big">{v.handOns.length}</b> {countWord(v.handOns.length, 'hand-on', 'hand-ons')} followed</span> : null}
              {latest && v.newSinceMorning > 0 && morning ? <span><b className="big">{v.newSinceMorning}</b> new since {morning}</span> : null}
              <span><b className="big">{v.conversations}</b> {countWord(v.conversations, 'conversation', 'conversations')} read</span>
          </p>
          <p className="ci-meta">
            {covered ? <b>{covered}</b> : null}
            {writtenWords(v).map((w) => <span key={w}>{w}</span>)}
          </p>
          {lineCount === 0 ? (
            // Nothing read is not the same news as nothing moved (B530): say which it was.
            v.conversations === 0 ? (
              <EmptyState className="ci-none" title="Nothing to read." note="No mail, chat or call you were not on reached Cobrain in this window." />
            ) : (
              <EmptyState className="ci-none" title="Nothing moved with a client, partner or deal." note={null} />
            )
          ) : null}

      {v.handOns.length > 0 ? (
        <section className="ci-sec" aria-labelledby={`${idp}ci-handons`}>
          <SectionHeading className="ci-sech" id={`${idp}ci-handons`} title="What came of your hand-ons" />
          {v.handOns.map((h: HandOnView) => (
            <CardFrame key={h.id} className="ci-card ci-ho">
              <h3 className="ci-name">{h.handedTo ? `Your hand-on to ${h.handedTo}` : 'Your hand-on'}</h3>
              <List className="ci-lines"><Line line={h} named={!h.handedTo} open={open === h.id} onToggle={() => toggle(h.id)} sources={sources[h.id]} go={go} /></List>
            </CardFrame>
          ))}
        </section>
      ) : null}

      {v.companies.length > 0 ? (
        <section className="ci-sec" aria-labelledby={`${idp}ci-companies`}>
          <SectionHeading className="ci-sech" id={`${idp}ci-companies`} title="Clients, partners and deals" />
          <ol className="ci-list">
            {v.companies.map((c) => (
              <li key={c.organisationId}><CardFrame className="ci-card ci-co">
                <CompanyHead store={v.store.tenantId} id={c.organisationId} name={c.name} label={c.label} owner={c.owner} go={go} />
                <List className="ci-lines">{c.lines.map((l) => <Line key={l.id} line={l} open={open === l.id} onToggle={() => toggle(l.id)} sources={sources[l.id]} go={go} />)}</List>
              </CardFrame></li>
            ))}
          </ol>
        </section>
      ) : null}
    </>
  );
}

/**
 * A company's head on the page (U353; the drawing's "Company intelligence, what moved"): its line, in the record's words,
 * once it is read; until then the label, the owner and the name, which is a link to the record either way (X30).
 */
function CompanyHead({ store, id, name, label, owner, go }: { store: string; id: string; name: string; label: string; owner: string | null; go: (path: string) => void }) {
  const line = useCompanyLine(store, id);
  if (line) return <CompanyLine line={line} go={go} />;
  return (
    <>
      <div className="ci-cmeta"><Chip>{label}</Chip>{owner ? <span>Owner {owner}</span> : null}</div>
      <h3 className="ci-name"><CompanyName id={id} name={name} go={go} /></h3>
    </>
  );
}

export function CompanyIntel({ go }: { go: (path: string) => void }) {
  const [day, setDay] = useState<string | null>(null);
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [open, setOpen] = useState<string | null>(null);
  const [sources, setSources] = useState<Record<string, Sources>>({});
  // B1694: the previous two weeks' days, each read the first time it is opened, and the date search.
  const [past, setPast] = useState<Record<string, Load>>({});
  const [query, setQuery] = useState('');
  const [note, setNote] = useState<string | null>(null);
  const fetchPast = useCallback((d: string) => {
    setPast((x) => ({ ...x, [d]: { kind: 'loading' } }));
    companyIntel(d, { fresh: true }).then((view) => setPast((x) => ({ ...x, [d]: { kind: 'ready', view } })),
      (e: unknown) => setPast((x) => ({ ...x, [d]: { kind: 'error', message: problem(e) } })));
  }, []);

  const fetchView = useCallback((d: string | null) => {
    setLoad({ kind: 'loading' });
    companyIntel(d, { fresh: true }).then((view) => setLoad({ kind: 'ready', view }), (e: unknown) => setLoad({ kind: 'error', message: problem(e) }));
  }, []);
  useEffect(() => { fetchView(day); }, [day, fetchView]);

  // Read again when he comes back to the page (B1263, live.ts): the day's reading moves as the
  // pipeline works through the morning's mail. In place -- the page is not blanked to a skeleton
  // to say so, and a re-read that fails leaves what is on the screen.
  useLiveRefresh(useCallback((signal: AbortSignal) => companyIntel(day, { fresh: true }).then(
    (view) => { if (!signal.aborted) setLoad({ kind: 'ready', view }); },
    () => { /* the page was working a moment ago; one failed re-read does not replace it */ },
  ), [day]));

  const pick = (d: string | null) => { setOpen(null); setDay(d); };
  const toggle = (id: string) => {
    if (open === id) { setOpen(null); return; }
    setOpen(id);
    if (sources[id]?.kind === 'ready') return;
    setSources((s) => ({ ...s, [id]: { kind: 'loading' } }));
    lineSources(id).then((r) => setSources((s) => ({ ...s, [id]: { kind: 'ready', list: r.sources } })),
      (e: unknown) => setSources((s) => ({ ...s, [id]: { kind: 'error', message: problem(e) } })));
  };

  const title = <h1 className="ci-title">Company intelligence</h1>;
  if (load.kind === 'loading') return <div className="cip"><div className="ci-top">{title}</div><div className="skeleton" aria-label="Reading the read-out"><i /><i /><i /></div></div>;
  if (load.kind === 'error') {
    return (
      <div className="cip"><div className="ci-top">{title}</div>
        <p className="error" role="alert">The read-out could not be loaded: {load.message.replace(/\.$/, '')}. <ActionButton reading type="button" className="link inline" onClick={() => fetchView(day)}>Try again</ActionButton></p>
      </div>
    );
  }

  const v = load.view;
  const latest = day === null;
  // Every day with a read-out, newest first: the API lists the shown day apart from the others.
  const days = [...(v.day ? [v.day] : []), ...v.days].filter((d, i, a) => a.indexOf(d) === i).sort().reverse();
  const tabs = days.slice(0, TABS);
  const older = days.slice(TABS);
  // B1694: every day held, for the search, and the previous two weeks below the newest read-out.
  const summary = new Map(v.history.map((h) => [h.day, h]));
  const oldest = v.history.at(-1)?.day ?? null;
  const today = todayOf();
  const pastDays = recentDays(v.history).filter((d) => d !== v.day && d !== today);
  const search = (d: string) => {
    setNote(null);
    if (!d) return;
    if (summary.has(d)) { setQuery(''); pick(d === v.history[0]?.day ? null : d); return; }
    setNote(noReadoutWords(d, oldest));
  };

  return (
    <div className="cip">
      <div className="ci-top">
        {title}
        {tabs.length > 0 ? (
          <nav className="ci-days" aria-label="Read-outs by day">
            {tabs.map((d, i) => (
              <ActionButton reading key={d} type="button" className={d === v.day ? 'on' : ''} aria-current={d === v.day ? 'page' : undefined}
                      onClick={() => pick(i === 0 ? null : d)}>{dayTab(d)}</ActionButton>
            ))}
            {older.length > 0 ? (
              <label className="ci-older">
                <span className="sr-only">An older day</span>
                <ActionSelect reading value={older.includes(v.day ?? '') ? v.day ?? '' : ''} onChange={(e) => { if (e.target.value) pick(e.target.value); }}>
                  <option value="">Older</option>
                  {older.map((d) => <option key={d} value={d}>{dayTab(d)}</option>)}
                </ActionSelect>
              </label>
            ) : null}
            {oldest ? (
              <label className="ci-older">
                <span className="sr-only">Go to a day</span>
                <ActionInput reading type="date" min={oldest} max={today} value={query} title="Go to a day" onChange={(e) => { setQuery(e.target.value); search(e.target.value); }} />
              </label>
            ) : null}
          </nav>
        ) : null}
      </div>
      {note ? <p className="ci-muted" role="status">{note}</p> : null}

      {v.day === null ? (
        v.failed ? <p className="error" role="alert">{v.failed}</p> : (
          <EmptyState className="ci-none" title="Nothing written yet." note="Cobrain writes the read-out as things come in." />
        )
      ) : (
        <Readout v={v} latest={latest} open={open} toggle={toggle} sources={sources} go={go} />
      )}

      {latest && v.day !== null && pastDays.length > 0 ? (
        <section className="ci-sec" aria-labelledby="ci-past">
          <SectionHeading className="ci-sech" id="ci-past" title="The last two weeks" />
          {pastDays.map((d) => {
            const sum = summary.get(d);
            if (!sum) return <div key={d} className="ci-card ci-past"><span className="ci-pasth"><b>{dayHeading(d)}</b><span>{daySummaryWords(undefined, d)}</span></span></div>;
            const l = past[d];
            return (
              <details key={d} className="ci-card ci-past" onToggle={(e) => { if ((e.currentTarget as HTMLDetailsElement).open && !past[d]) fetchPast(d); }}>
                <summary className="ci-pasth"><b>{dayHeading(d)}</b><span>{daySummaryWords(sum, d)}</span></summary>
                <div className="ci-pastbody">
                  {!l || l.kind === 'loading' ? <div className="skeleton" aria-label="Reading the day"><i /><i /></div>
                    : l.kind === 'error' ? <p className="error" role="alert">This day could not be loaded: {l.message.replace(/\.$/, '')}. <ActionButton reading type="button" className="link inline" onClick={() => fetchPast(d)}>Try again</ActionButton></p>
                    : <Readout v={l.view} latest={false} idp={`${d}-`} open={open} toggle={toggle} sources={sources} go={go} />}
                </div>
              </details>
            );
          })}
        </section>
      ) : null}
    </div>
  );
}
